import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs';
import {
  getUserSettingsCulture,
  getUserSettingsCurrency,
  getUserSettingsLanguage,
  getUserSettingsTimezone
} from '@capital-access/common/globalization';
import { LocalizationService } from '@capital-access/common/localization';
import { getSelectedSecurity, getSelectedSecuritySurveillancePermission } from '@capital-access/common/security';
import { NotificationService } from '@capital-access/firefly/components';
import {
  createOwnershipAnalysisReport,
  createOwnershipAnalysisReportFinished,
  getOwnershipAnalysisSelectedList,
  OutputFormat
} from '@capital-access/reporting/common';
import { LOCALIZATION_KEY } from '../../constants';
import { generateReport } from '../../core/+state/report.actions';
import { OwnershipSource } from '../../core/models/ownership-source.enum';
import { FileNameService } from '../../core/services/file-name.service';
import { OwnershipAnalysisReportRequest } from '../models/ownership-analysis-report.model';

@Injectable()
export class OwnershipAnalysisReportEffects {
  createReportEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createOwnershipAnalysisReport),
      withLatestFrom(
        this.store.select(getUserSettingsCulture),
        this.store.select(getUserSettingsLanguage),
        this.store.select(getUserSettingsCurrency),
        this.store.select(getUserSettingsTimezone),
        this.store.select(getSelectedSecuritySurveillancePermission),
        this.store.select(getSelectedSecurity),
        this.store.select(getOwnershipAnalysisSelectedList)
      ),
      mergeMap(
        ([
          request,
          culture,
          language,
          currency,
          timeZone,
          isSrvPermissioned,
          security,
          ownershipAnalysisSelectedSecurityList
        ]) => {
          const isSrv = security?.surveillance ?? false;
          const ownershipAnalysiselectedSecurity = ownershipAnalysisSelectedSecurityList.find(
            item => item.id === request.securityId
          );
          return this.fileNameService
            .getDefaultFileName(isSrvPermissioned, isSrv, ownershipAnalysiselectedSecurity?.name ?? '', timeZone)
            .pipe(
              map(fileName => {
                const reportRequest: OwnershipAnalysisReportRequest = {
                  ...request,
                  culture,
                  language,
                  currency,
                  pullSampleData: true,
                  fileName: fileName,
                  timeZone,
                  dataSource: isSrv ? OwnershipSource.Surveillance : OwnershipSource.Public,
                  outputFormat: OutputFormat.Pdf
                };
                return reportRequest;
              }),

              switchMap(reportRequest =>
                this.localization.localize('ownershipAnalysisExportingStarted', {}, { scope: LOCALIZATION_KEY }).pipe(
                  tap(message => this.notificationService.notify(message, { type: 'info' })),
                  map(() => reportRequest)
                )
              ),
              map(reportRequest => {
                const successMessage = `ownershipAnalysisReportCreated`;
                const failureMessage = `ownershipAnalysisReportFailed`;
                return generateReport({
                  request: reportRequest,
                  url: 'configurable-reporting/ownershipAnalysis/launch',
                  successMessage,
                  failureMessage,
                  finishAction: createOwnershipAnalysisReportFinished()
                });
              })
            );
        }
      )
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private localization: LocalizationService,
    private notificationService: NotificationService,
    private fileNameService: FileNameService
  ) {}
}
